// src/views/Admin/Companies/components/CleanCompaniesModal/CleanCompaniesModal.tsx

import React, { useState, useEffect } from 'react';
import { HiOutlineExclamationTriangle, HiOutlineXMark, HiOutlineArchiveBoxXMark, HiOutlineCheck } from 'react-icons/hi2';
import { EmptyCompanyPreview } from '../../../../../crud/configuration_api/companies';
import './CleanCompaniesModal.scss';

interface CleanCompaniesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFetchPreview: () => Promise<EmptyCompanyPreview[]>;
  onConfirm: () => Promise<void>;
}

const CleanCompaniesModal: React.FC<CleanCompaniesModalProps> = ({
  isOpen,
  onClose,
  onFetchPreview,
  onConfirm
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emptyCompanies, setEmptyCompanies] = useState<EmptyCompanyPreview[]>([]);

  const isConfirmEnabled = inputValue.trim() === 'ELIMINAR';

  // Fetch preview when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPreview();
    } else {
      // Reset state when modal closes
      setInputValue('');
      setError(null);
      setEmptyCompanies([]);
    }
  }, [isOpen]);

  const fetchPreview = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const companies = await onFetchPreview();
      setEmptyCompanies(companies);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al obtener compañías vacías');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isConfirmEnabled) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onConfirm();
      setInputValue('');
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al limpiar compañías');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setInputValue('');
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isConfirmEnabled && !isDeleting && emptyCompanies.length > 0) {
      handleConfirm();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="clean-modal-overlay" onClick={handleClose}>
      <div className="clean-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={handleClose} disabled={isDeleting}>
          <HiOutlineXMark size={20} />
        </button>

        <div className="modal-icon">
          <HiOutlineArchiveBoxXMark size={32} />
        </div>

        <h2>Limpiar Compañías Vacías</h2>
        
        <div className="warning-box">
          <p><strong>¡ATENCIÓN! Esta acción es extremadamente peligrosa.</strong></p>
          <p>Se eliminarán permanentemente todas las compañías sin locales:</p>
          <ul>
            <li>Usuarios de la compañía</li>
            <li>Proveedores y categorías</li>
            <li>Unidades de almacenamiento</li>
            <li>Códigos de creación de cuenta</li>
          </ul>
        </div>

        {isLoading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Buscando compañías vacías...</p>
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : emptyCompanies.length === 0 ? (
          <div className="empty-state">
            <HiOutlineCheck size={48} />
            <p>No hay compañías vacías para limpiar</p>
          </div>
        ) : (
          <>
            <div className="companies-list">
              <h4>Se encontraron {emptyCompanies.length} compañías vacías:</h4>
              <div className="list-container">
                {emptyCompanies.map(company => (
                  <div key={company.company_id} className="company-item">
                    <div className="company-info">
                      <span className="name">{company.company_name}</span>
                      <span className="email">{company.company_email}</span>
                    </div>
                    <div className="dependencies">
                      {company.dependencies.providers > 0 && (
                        <span className="badge">{company.dependencies.providers} prov.</span>
                      )}
                      {company.dependencies.users > 0 && (
                        <span className="badge">{company.dependencies.users} users</span>
                      )}
                      {company.dependencies.storage_units > 0 && (
                        <span className="badge">{company.dependencies.storage_units} alm.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="confirmation-section">
              <p>
                Para confirmar, escribe <strong>"ELIMINAR"</strong> a continuación:
              </p>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='Escribe "ELIMINAR" para confirmar'
                disabled={isDeleting}
                autoFocus
              />
            </div>
          </>
        )}

        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancelar
          </button>
          {emptyCompanies.length > 0 && !isLoading && (
            <button 
              className="delete-button"
              onClick={handleConfirm}
              disabled={!isConfirmEnabled || isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="btn-spinner" />
                  Eliminando...
                </>
              ) : (
                `Eliminar ${emptyCompanies.length} Compañías`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CleanCompaniesModal;
